"use client";

import * as React from "react";

// --- Hooks ---
import { useMenuNavigation } from "../../../hooks/use-menu-navigation";

// --- Lib ---
import { getElementOverflowPosition } from "../../../lib/tiptap-collab-utils";

// --- UI Primitives ---
import { Button, ButtonGroup } from "../../tiptap-ui-primitive/button";
import { Card, CardBody, CardHeader } from "../../tiptap-ui-primitive/card";
import { Input } from "../../tiptap-ui-primitive/input";

// --- Styles ---
import "./emoji-menu.scss";

export type Emoji = {
  /**
   * A unique name of the emoji which will be stored as attribute
   */
  name: string;
  /**
   * The emoji unicode character
   */
  emoji?: string;
  /**
   * A list of unique shortcodes that are used by input rules to find the emoji
   */
  shortcodes: string[];
  /**
   * A list of tags that can help for searching emojis
   */
  tags: string[];
  /**
   * A name that can help to group emojis
   */
  group?: string;
  /**
   * A list of unique emoticons
   */
  emoticons?: string[];
  /**
   * The unicode version the emoji was introduced
   */
  version?: number;
  /**
   * A fallback image if the current system doesn't support the emoji or for custom emojis
   */
  fallbackImage?: string;
  /**
   * Store some custom data
   */
  [key: string]: unknown;
};

const MAX_SUGGESTIONS = 100;
const SORT_EMOJIS = <T extends Emoji>(a: T, b: T) =>
  a.name.localeCompare(b.name);

export const searchEmojiData = <T extends Emoji>(
  query: string,
  emojiData: T,
): boolean => {
  const lowercaseQuery = query.toLowerCase().trim();

  return (
    emojiData.name.toLowerCase().includes(lowercaseQuery) ||
    emojiData.shortcodes.some((shortName) =>
      shortName.toLowerCase().includes(lowercaseQuery),
    ) ||
    emojiData.tags.some((tag) => tag.toLowerCase().includes(lowercaseQuery))
  );
};

export const getFilteredEmojis = <T extends Emoji>(props: {
  query: string;
  emojis: T[];
}) => {
  const { query, emojis } = props;
  const trimmedQuery = query.trim();

  const filteredEmojis = !trimmedQuery
    ? emojis.slice(0, MAX_SUGGESTIONS)
    : emojis
        .filter((emoji) => searchEmojiData(trimmedQuery, emoji))
        .slice(0, MAX_SUGGESTIONS);

  return filteredEmojis.sort(SORT_EMOJIS);
};

export interface EmojiMenuItemProps<T extends Emoji> {
  emoji: T;
  index: number;
  isSelected: boolean;
  onSelect: (emoji: T) => void;
  selector: string;
}

export const EmojiMenuItem = <T extends Emoji>(
  props: EmojiMenuItemProps<T>,
) => {
  const { emoji, isSelected, onSelect, selector } = props;
  const itemRef = React.useRef<HTMLButtonElement>(null);

  React.useEffect(() => {
    const menuElement = document.querySelector(selector) as HTMLElement;
    if (!itemRef.current || !isSelected || !menuElement) return;

    const overflow = getElementOverflowPosition(itemRef.current, menuElement);
    if (overflow === "top") {
      itemRef.current.scrollIntoView(true);
    } else if (overflow === "bottom") {
      itemRef.current.scrollIntoView(false);
    }
  }, [isSelected, selector]);

  if (!emoji) return null;

  return (
    <Button
      ref={itemRef}
      data-style="ghost"
      data-active-state={isSelected ? "on" : "off"}
      onClick={() => onSelect(emoji)}
    >
      {emoji.fallbackImage ? (
        <img
          className="tiptap-button-emoji"
          src={emoji.fallbackImage}
          alt={emoji.name}
        />
      ) : (
        <span className="tiptap-button-emoji">{emoji.emoji}</span>
      )}
      <span className="tiptap-button-text">:{emoji.name}:</span>
    </Button>
  );
};

export interface EmojiMenuProps<T extends Emoji> {
  emojis: T[];
  onSelect: (emoji: T) => void;
  onClose?: () => void;
  showSearch?: boolean;
  selector?: string;
}

export const EmojiMenu = <T extends Emoji>(props: EmojiMenuProps<T>) => {
  const {
    emojis,
    onSelect,
    onClose,
    showSearch = false,
    selector = ".emoji-menu-list",
  } = props;

  const containerRef = React.useRef<HTMLDivElement>(null);
  const searchRef = React.useRef<HTMLInputElement>(null);

  const [searchQuery, setSearchQuery] = React.useState("");

  const filteredEmojis = React.useMemo(() => {
    const filtered = getFilteredEmojis({ query: searchQuery, emojis });
    return filtered;
  }, [emojis, searchQuery]);

  const { selectedIndex } = useMenuNavigation({
    containerRef,
    query: searchQuery,
    items: filteredEmojis,
    onSelect,
    onClose,
  });

  const renderedItems = React.useMemo(() => {
    return filteredEmojis.map((emoji, index) => (
      <EmojiMenuItem
        key={emoji.name}
        emoji={emoji}
        index={index}
        isSelected={index === selectedIndex}
        onSelect={() => onSelect(emoji)}
        selector={selector}
      />
    ));
  }, [filteredEmojis, selectedIndex, onSelect, selector]);

  return (
    <Card ref={containerRef}>
      {showSearch && (
        <CardHeader>
          <Input
            ref={searchRef}
            type="text"
            placeholder="Add a emoji reaction..."
            value={searchQuery}
            autoFocus
            onChange={(e) => setSearchQuery(e.target.value)}
            className="emoji-menu-search-input"
          />
        </CardHeader>
      )}

      {renderedItems.length && (
        <CardBody className="emoji-menu-list">
          <ButtonGroup>{renderedItems}</ButtonGroup>
        </CardBody>
      )}
    </Card>
  );
};
